import React, { useState, useEffect, useCallback } from 'react';
import { feeStructuresApi, configApi, invoicesApi } from '../services/api';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { BulkSelectionBar } from '../components/BulkDelete';
import { Plus, Loader2, FileText, AlertCircle, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'UGX') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

const FeeStructurePage = () => {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [branchFilter, setBranchFilter] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const [newStructure, setNewStructure] = useState({
    branch: '', fee_head: '', sub_category: '', class_name: '',
    term: '', amount: '', currency: 'USD', is_mandatory: false
  });

  const handleFeeHeadChange = (v) => setNewStructure((prev) => ({ ...prev, fee_head: v }));

  const [bulkInvoice, setBulkInvoice] = useState({
    academic_year: '2025-2026', term: '', branch: '', class_name: '', due_date: ''
  });

  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await configApi.get();
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  const fetchStructures = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchFilter.length) params.branch = branchFilter.join(',');

      const response = await feeStructuresApi.getAll(params);
      setStructures(response.data.fee_structures || response.data || []);
    } catch (err) {
      toast.error('Failed to load fee structures');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchStructures(); }, [fetchStructures]);

  const handleAddStructure = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await feeStructuresApi.create({ ...newStructure, amount: parseFloat(newStructure.amount) });
      toast.success('Fee structure created successfully');
      setShowAddDialog(false);
      setNewStructure({ branch: '', fee_head: '', sub_category: '', class_name: '', term: '', amount: '', currency: 'UGX', is_mandatory: false });
      fetchStructures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create fee structure');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview which fee_structures will fire when bulk-generate runs.
  useEffect(() => {
    if (!showBulkDialog || !bulkInvoice.term) {
      setBulkPreview([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setBulkPreviewLoading(true);
      try {
        const params = { term: bulkInvoice.term, limit: 500 };
        if (bulkInvoice.branch) params.branch = bulkInvoice.branch;
        if (bulkInvoice.class_name) params.class_name = bulkInvoice.class_name;
        const res = await feeStructuresApi.getAll(params);
        if (!cancelled) {
          const all = res.data.fee_structures || res.data || [];
          // Only mandatory entries are honored by the bulk endpoint; exclude surcharge.
          setBulkPreview(all.filter(f => f.is_mandatory && (f.fee_head || '').toLowerCase() !== 'surcharge'));
        }
      } catch (err) {
        if (!cancelled) setBulkPreview([]);
      } finally {
        if (!cancelled) setBulkPreviewLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showBulkDialog, bulkInvoice.term, bulkInvoice.branch, bulkInvoice.class_name]);

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await invoicesApi.generateBulk({
        academic_year: bulkInvoice.academic_year, term: bulkInvoice.term,
        branch: bulkInvoice.branch || null, class_name: bulkInvoice.class_name || null,
        due_date: bulkInvoice.due_date || null
      });
      toast.success(response.data.message);
      setShowBulkDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected fee structures?`)) return;

    setDeleting(true);
    try {
      await feeStructuresApi.bulkDelete(Array.from(selectedIds));
      toast.success(`Deleted ${selectedIds.size} fee structures`);
      setSelectedIds(new Set());
      fetchStructures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleSingleDelete = async (id) => {
    if (!window.confirm('Delete this fee structure?')) return;
    try {
      await feeStructuresApi.delete(id);
      toast.success('Fee structure deleted');
      fetchStructures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await feeStructuresApi.uploadExcel(formData);
      toast.success(response.data.message);
      fetchStructures();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllForHead = (head) => {
    const headStructures = groupedStructures[head] || [];
    const allSelected = headStructures.every(s => selectedIds.has(s.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      headStructures.forEach(s => { if (allSelected) next.delete(s.id); else next.add(s.id); });
      return next;
    });
  };

  const groupedStructures = structures.reduce((acc, structure) => {
    const head = structure.fee_head;
    if (!acc[head]) acc[head] = [];
    acc[head].push(structure);
    return acc;
  }, {});

  const feeHeads = [
    'tuition', 'club', 'uniform', 'lunch', 'transport', 'examination',
    'id_cards', 'registration', 'capital_levy', 'day_care', 'field_trips',
    'jersey', 'textbooks', 'locker_keys', 'others'
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Fee Structure</h1>
          <p className="text-[#5C635F] mt-1">Configure fees for each academic year, term, and class</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Delete-selected moved to the floating bar at the bottom, so ticking a
              row never reflows this header. */}
          <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} className="hidden" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-excel-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload Excel
          </Button>
          <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="generate-invoices-btn">
                <FileText className="w-4 h-4 mr-2" />Generate Invoices
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bulk Generate Invoices</DialogTitle></DialogHeader>
              <form onSubmit={handleBulkGenerate} className="space-y-4">
                <div className="bg-[#D99C3B]/10 border border-[#D99C3B]/20 text-[#D99C3B] px-4 py-3 rounded-lg flex items-start gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Creates <b>one combined invoice per student</b> (all mandatory fees as line items). Inactive and Left students are skipped, and students who already have an invoice for this term are not duplicated.</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Academic Year</Label>
                    <Select value={bulkInvoice.academic_year} onValueChange={(v) => setBulkInvoice({ ...bulkInvoice, academic_year: v })}>
                      <SelectTrigger data-testid="bulk-academic-year"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Term</Label>
                    <Select value={bulkInvoice.term} onValueChange={(v) => setBulkInvoice({ ...bulkInvoice, term: v })}>
                      <SelectTrigger data-testid="bulk-term"><SelectValue placeholder="Select term" /></SelectTrigger>
                      <SelectContent>
                        {config?.terms?.map((term) => <SelectItem key={term} value={term}>{term}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Branch (Optional)</Label>
                    <Select value={bulkInvoice.branch || "all"} onValueChange={(v) => setBulkInvoice({ ...bulkInvoice, branch: v === "all" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="All branches" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Branches</SelectItem>
                        {config?.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Class (Optional)</Label>
                    <Select value={bulkInvoice.class_name || "all"} onValueChange={(v) => setBulkInvoice({ ...bulkInvoice, class_name: v === "all" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="All classes" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {config?.classes?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input type="date" value={bulkInvoice.due_date}
                    onChange={(e) => setBulkInvoice({ ...bulkInvoice, due_date: e.target.value })}
                    data-testid="bulk-due-date" />
                  <p className="text-xs text-[#8A8E8B]">Printed on every invoice as the payment due date. Leave blank for “On Receipt”.</p>
                </div>
                {/* Preview of fee structures that will be applied */}
                {bulkInvoice.term && (
                  <div className="border border-[#D8D9E8] rounded-lg overflow-hidden">
                    <div className="bg-[#F5F5FA] px-4 py-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#1C1F1D]">
                        Fee structures that will be applied
                      </span>
                      <span className="text-xs text-[#5C635F]">
                        {bulkPreviewLoading ? 'loading…' : `${bulkPreview.length} item${bulkPreview.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                    {bulkPreview.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white">
                              <TableHead className="text-xs">Fee Head</TableHead>
                              <TableHead className="text-xs">Sub Category</TableHead>
                              <TableHead className="text-xs">Class</TableHead>
                              <TableHead className="text-xs">Branch</TableHead>
                              <TableHead className="text-xs text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkPreview.map((f) => (
                              <TableRow key={f.id}>
                                <TableCell className="text-xs">
                                  {config?.fee_head_display?.[f.fee_head] || (f.fee_head || '').replace(/_/g, ' ')}
                                </TableCell>
                                <TableCell className="text-xs text-[#5C635F]">{f.sub_category || '—'}</TableCell>
                                <TableCell className="text-xs">{f.class_name}</TableCell>
                                <TableCell className="text-xs">{f.branch}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums">
                                  {f.currency} {Number(f.amount || 0).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      !bulkPreviewLoading && (
                        <div className="px-4 py-3 text-xs text-[#5C635F]">
                          No mandatory fee structures match this combination. Bulk generation will produce 0 invoices.
                        </div>
                      )
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting || !bulkInvoice.term || bulkPreview.length === 0} data-testid="confirm-generate-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Generate Invoices
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-fee-structure-btn">
                <Plus className="w-4 h-4 mr-2" />Add Structure
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Add Fee Structure</DialogTitle></DialogHeader>
              <form onSubmit={handleAddStructure} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Branch</Label>
                    <Select value={newStructure.branch} onValueChange={(v) => setNewStructure({ ...newStructure, branch: v })}>
                      <SelectTrigger data-testid="fee-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {config?.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Select value={newStructure.class_name} onValueChange={(v) => setNewStructure({ ...newStructure, class_name: v })}>
                      <SelectTrigger data-testid="fee-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                      <SelectContent>
                        {config?.classes?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fee Head</Label>
                    <Select value={newStructure.fee_head} onValueChange={handleFeeHeadChange}>
                      <SelectTrigger data-testid="fee-head-select"><SelectValue placeholder="Select fee head" /></SelectTrigger>
                      <SelectContent>
                        {feeHeads.map((h) => (
                          <SelectItem key={h} value={h}>
                            {config?.fee_head_display?.[h] || h.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sub Category <span className="text-xs text-[#5C635F]">(optional)</span></Label>
                    <Input
                      value={newStructure.sub_category}
                      onChange={(e) => setNewStructure({ ...newStructure, sub_category: e.target.value })}
                      placeholder="e.g. Regular, Premium, Local trip"
                      data-testid="fee-sub-category"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Term</Label>
                  <Select value={newStructure.term} onValueChange={(v) => setNewStructure({ ...newStructure, term: v })}>
                    <SelectTrigger data-testid="fee-term-select"><SelectValue placeholder="Select term" /></SelectTrigger>
                    <SelectContent>
                      {config?.terms?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      value={newStructure.amount}
                      onChange={(e) => setNewStructure({ ...newStructure, amount: e.target.value })}
                      required
                      min="0"
                      step="1"
                      data-testid="fee-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <CurrencySelector
                      value={newStructure.currency}
                      onChange={(v) => setNewStructure({ ...newStructure, currency: v })}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={newStructure.is_mandatory} onCheckedChange={(v) => setNewStructure({ ...newStructure, is_mandatory: v })} />
                  <Label>Mandatory Fee</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-fee-structure-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Create Structure
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <MultiSelect widthClass="w-full sm:w-40" placeholder="All Branches" testId="branch-filter"
              options={config?.branches || []} selected={branchFilter} onChange={setBranchFilter} />
          </div>
        </CardContent>
      </Card>

      {/* Fee Structures by Head */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
        </div>
      ) : (
        <Tabs defaultValue="tuition" className="space-y-4">
          <TabsList className="bg-[#ECEDF5] flex-wrap h-auto gap-1 p-1">
            {feeHeads.map((head) => (
              <TabsTrigger key={head} value={head} data-testid={`tab-${head}`}>
                {config?.fee_head_display?.[head] || head.replace(/_/g, ' ')}
              </TabsTrigger>
            ))}
          </TabsList>

          {feeHeads.map((head) => {
            const headStructures = groupedStructures[head] || [];
            const allSelected = headStructures.length > 0 && headStructures.every(s => selectedIds.has(s.id));
            return (
              <TabsContent key={head} value={head}>
                <Card className="border-[#D8D9E8]">
                  <CardHeader>
                    <CardTitle className="text-lg">{config?.fee_head_display?.[head] || head.replace(/_/g, ' ')} Fee Structure</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {headStructures.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-[#F5F5FA]">
                              <TableHead className="w-10">
                                <Checkbox checked={allSelected} onCheckedChange={() => toggleSelectAllForHead(head)} data-testid={`select-all-${head}`} />
                              </TableHead>
                              <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                              <TableHead className="text-xs uppercase font-semibold">Sub Category</TableHead>
                              <TableHead className="text-xs uppercase font-semibold">Term</TableHead>
                              <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                              <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                              <TableHead className="text-xs uppercase font-semibold">Mandatory</TableHead>
                              <TableHead className="text-xs uppercase font-semibold w-10">Del</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {headStructures.map((structure) => (
                              <TableRow key={structure.id} data-testid={`fee-row-${structure.id}`}>
                                <TableCell>
                                  <Checkbox checked={selectedIds.has(structure.id)} onCheckedChange={() => toggleSelect(structure.id)} />
                                </TableCell>
                                <TableCell className="font-medium">{structure.class_name}</TableCell>
                                <TableCell className="text-[#5C635F]">{structure.sub_category || '—'}</TableCell>
                                <TableCell>{structure.term}</TableCell>
                                <TableCell>{structure.branch}</TableCell>
                                <TableCell className="text-right tabular-nums font-medium">{formatCurrency(structure.amount, structure.currency)}</TableCell>
                                <TableCell>
                                  <Badge className={structure.is_mandatory ? 'bg-[#3E3F96]/10 text-[#3E3F96]' : 'bg-gray-100 text-gray-600'}>
                                    {structure.is_mandatory ? 'Yes' : 'No'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleSingleDelete(structure.id)} data-testid={`delete-fee-${structure.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-[#5C635F]">
                        No {head} fee structures found for the selected filters
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* Floating selection bar — consistent with the other multi-select modules. */}
      <BulkSelectionBar
        count={selectedIds.size}
        label={selectedIds.size === 1 ? 'fee selected' : 'fees selected'}
        onClear={() => setSelectedIds(new Set())}
      >
        <Button variant="outline" size="sm" className="text-[#C85A40] border-[#C85A40] hover:bg-[#C85A40]/10"
          onClick={handleBulkDelete} disabled={deleting} data-testid="bulk-delete-fee-btn">
          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Delete
        </Button>
      </BulkSelectionBar>
    </div>
  );
};

export default FeeStructurePage;
